import React from "react";
import { Card, Col, Row, Table } from "react-bootstrap";

/**
 * In-dashboard wiki: how Retail & Wholesale (R&W) vendors operate across
 * Lusuku — onboarding, catalogue, shopper surfaces, delivery, COD thresholds,
 * and how R&W money settles.
 *
 * IMPORTANT — content policy for this file:
 *   - No credentials, API keys, tokens, connection strings.
 *   - No internal hostnames or ports that aren't already public.
 *   - Development-only guidance must be gated behind NODE_ENV so CRA
 *     dead-code-eliminates it from the production bundle.
 */
const IS_DEV = process.env.NODE_ENV === "development";

const ACCENT = "#FF6B1A";
const TINT = "#FF6B1A1f";
const PILLTEXT = "#a8480e";

const titleStyle = { fontSize: 13.5, fontWeight: 700, color: "#1a2b4a" };
const bodyText = { fontSize: 12.5 };
const listStyle = { ...bodyText, paddingLeft: 18 };
const cardStyle = { border: "1px solid #e5e9f1", borderRadius: 8, boxShadow: "none" };
const tableStyle = { fontSize: 12 };
const theadStyle = { background: "#f5f7fb" };

const pillStyle = {
  display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10,
  fontWeight: 700, letterSpacing: "0.3px", textTransform: "uppercase",
  background: TINT, color: PILLTEXT, marginRight: 6, marginBottom: 4,
};

const Pill = ({ label }) => <span style={pillStyle}>{label}</span>;

const SectionIcon = ({ cls }) => (
  <i className={cls} style={{ fontSize: 15, color: "#1a2b4a", marginRight: 8, verticalAlign: "middle" }} />
);

const QA = ({ q, a }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2b4a" }}>{q}</div>
    <div className="text-muted" style={{ fontSize: 12.5, marginTop: 1 }}>{a}</div>
  </div>
);

const RetailWholesaleWiki = () => {
  return (
    <div>
      {/* Compact header */}
      <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 12, marginBottom: 18 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#1a2b4a" }}>
          Retail &amp; Wholesale on Lusuku
        </div>
        <div style={{ fontSize: 12.5, color: "#6b7a99", marginTop: 2 }}>
          Staff reference for Retail, Wholesale and Online Retail vendors: onboarding, catalogue,
          shopper surfaces, delivery, COD thresholds and settlement.
        </div>
      </div>

      {IS_DEV && (
        <div
          style={{
            background: "#fff8ea", border: "1px solid #f1e0a8", borderRadius: 8,
            padding: "10px 14px", marginBottom: 16, fontSize: 12.5,
          }}
        >
          <div style={{ fontWeight: 700, color: "#7a5a00", marginBottom: 2 }}>Local development only</div>
          <div className="text-muted">
            Exercise R&amp;W flows against a locally seeded vendor: register a seller, verify them
            from <code>/admin/rw-vendors</code>, add a product, then order from the web storefront.
            Seed commands and test credentials live outside this UI, which is served to all
            supervisors in production.
          </div>
        </div>
      )}

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-badge" />
            At a glance — what an R&amp;W vendor actually is
          </Card.Title>
          <div className="mb-2">
            <Pill label="Retail" />
            <Pill label="Wholesale" />
            <Pill label="Online Retail" />
            <Pill label="Outlets" />
            <Pill label="Expo + Web shopper" />
            <Pill label="COD thresholds" />
          </div>
          <p className="text-muted mb-0" style={bodyText}>
            Retail &amp; wholesale (&ldquo;R&amp;W&rdquo;) vendors sell general merchandise and
            bulk goods through the <code>RetailerWholesaler</code> tenant type, shoppable on{" "}
            <strong>both</strong> the Expo app and the web storefront. Vendors may own multiple
            outlets; every outlet inherits the parent vendor&rsquo;s operation mode.
          </p>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-badge" />
                1. Onboarding &amp; verification
              </Card.Title>
              <p className="text-muted mb-2" style={bodyText}>
                Public vendor registration creates RESTAURANT / RETAILER / WHOLESALER / HEALTHCARE
                sellers. A <code>RETAILER</code> or <code>WHOLESALER</code> registration
                auto-creates the matching <code>RetailerWholesaler</code> vendor document with{" "}
                <code>sellerVerification: PENDING</code>. Staff verify the seller from{" "}
                <strong>R &amp; W → Vendors</strong>.
              </p>
              <ul className="text-muted mb-0" style={listStyle}>
                <li>
                  Only <code>VERIFIED</code> sellers can create products — the write gate requires
                  a valid login, a RETAILER/WHOLESALER operation mode, verified status, and that
                  the product belongs to the requesting vendor.
                </li>
                <li>
                  Pipeline: Register → <code>PENDING</code> → staff verify → <code>VERIFIED</code>{" "}
                  → can create products → staff optionally mark <code>promoted</code> → appears in
                  the Top Vendors carousel.
                </li>
                <li>
                  A <code>REJECTED</code> seller behaves like a pending one: no product creation,
                  hidden from shopper listings.
                </li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-layers-3" />
                2. The three operation modes
              </Card.Title>
              <ul className="text-muted mb-0" style={listStyle}>
                <li>
                  <strong>Retail</strong> — general merchandise in everyday quantities. Platform
                  commission <strong>5%</strong>.
                </li>
                <li>
                  <strong>Wholesale</strong> — bulk ordering: minimum order volumes, bulk-pricing
                  support, and a lower platform commission (<strong>3%</strong>) than retail.
                </li>
                <li>
                  <strong>Online Retail</strong> — a retail-style mode for online sellers; shares
                  the R&amp;W catalogue and order pipeline.
                </li>
              </ul>
              <p className="text-muted mb-0 mt-2" style={bodyText}>
                (For context: restaurants pay 8% — R&amp;W rates are deliberately lower.)
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-box" />
            3. Catalogue &amp; stock
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            R&amp;W products are organised by category/section with unit measurements
            (<code>siUnits</code>), variants via option groups, and hosted images. Stock is
            tracked per product (<code>countInStock</code>) with out-of-stock auto-disable.
          </p>
          <ul className="text-muted mb-0" style={listStyle}>
            <li>
              <strong>Two separate retail pipelines — they never cross.</strong> Dashboard-created
              products live in <code>retailerwholesalerproducts</code> and feed the mobile retail
              screen and web vendor shop pages. The separate web-shop listings pipeline
              (<code>PostListing</code>) feeds only the main web shop and needs its own admin
              verification — a dashboard product never appears there unless it is also created as
              a web listing and verified.
            </li>
            <li>
              Products go through approval before going live:{" "}
              <code>PENDING → VERIFIED / REJECTED</code>, with verifier and date recorded. Staff
              can QC a live item via the <strong>Improve</strong> flow, which reuses the
              vendor&rsquo;s own product-edit form.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-tv-2" />
            4. Ordering surfaces &amp; customer flow
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            Customers shop R&amp;W on the Expo retail screen and on web vendor shop pages. The web
            cart is <strong>multi-vendor</strong> — items from several R&amp;W vendors at once, in
            bulk quantities — then checkout runs shipping → payment → place order.
          </p>
          <Table responsive hover size="sm" className="mb-2" style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>Surface</th>
                <th>Exact eligibility rules</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Top Vendors carousel (web landing)</td>
                <td className="text-muted">
                  Vendor is active <strong>and</strong> promoted. Promotion is a manual staff action — verification and product count do not matter here.
                </td>
              </tr>
              <tr>
                <td>All Vendors page (web)</td>
                <td className="text-muted">
                  Active + owner <code>VERIFIED</code> + at least one dashboard product. Promoted vendors float to the top with a Featured badge.
                </td>
              </tr>
              <tr>
                <td>Mobile retail screen (Expo)</td>
                <td className="text-muted">
                  Products of active vendors only — items from deactivated vendors are stripped before the response. Unverified vendors can&rsquo;t create products at all.
                </td>
              </tr>
              <tr>
                <td>Direct vendor shop link</td>
                <td className="text-muted">
                  Any vendor by owner ID — no promotion requirement; shared links always work by design.
                </td>
              </tr>
            </tbody>
          </Table>
          <Table responsive size="sm" className="mb-0" style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>Vendor state</th>
                <th>Top Vendors</th>
                <th>All Vendors</th>
                <th>Mobile products</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Active, verified, promoted, has products", "Yes", "Yes (Featured)", "Yes"],
                ["Active, verified, not promoted, has products", "No", "Yes", "Yes"],
                ["Active, verified, no products yet", "No", "No", "No"],
                ["Pending / rejected verification", "No", "No", "No (can’t create products)"],
                ["Deactivated", "No", "No", "No"],
              ].map(([state, top, all, mobile]) => (
                <tr key={state}>
                  <td className="text-muted">{state}</td>
                  <td>{top}</td>
                  <td>{all}</td>
                  <td>{mobile}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-delivery-fast" />
            5. Order lifecycle, delivery &amp; COD rules
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            R&amp;W orders ride the platform state machine{" "}
            <code>PLACED → CONFIRMED → READY → ASSIGNED → PICKED_UP → DELIVERED → SETTLED</code>.
            Delivery fees come from the server-authoritative signed-quote engine; client-side
            amounts are display-only.
          </p>
          <ul className="text-muted mb-0" style={listStyle}>
            <li>
              <strong>Three fulfilment modes:</strong> the vendor&rsquo;s own riders, authorised
              3PL partner riders, or Lusuku dispatch. A vendor marking their own order DELIVERED
              triggers the same escrow delivery-confirmation as a rider path.
            </li>
            <li>
              <strong>COD cash threshold cycle.</strong> Every COD delivery adds the collected
              cash to the rider&rsquo;s digitally tracked held balance. When it reaches the
              rider&rsquo;s threshold (configurable per rider, with a platform-wide default), the
              rider is <strong>flagged</strong>: they stop receiving new assignments and see a
              deposit banner in the rider app.
            </li>
            <li>
              <strong>Deposit &amp; auto-unflag.</strong> The rider taps &ldquo;Deposit to
              Lusuku&rdquo;, which sends an MTN MoMo PIN prompt to their registered MTN number (an
              override number is allowed; non-MTN numbers are rejected up front). On approval the
              deposit is confirmed, the rider is automatically unflagged and the held balance
              resets to zero. A decline or failure leaves them flagged with retry available; a
              background check every few minutes catches confirmations the network delivers late,
              so a successful deposit never leaves a rider stuck.
            </li>
            <li>
              <strong>Staff-side handling.</strong> A red <strong>COD Limit</strong> badge marks
              flagged riders in assignment dropdowns. Confirmed deposits sit in the COD
              reconciliation view until staff allocate the cash to vendor wallets. If a rider cannot
              deposit by MTN, staff with wallet rights take the cash and press
              <strong>Receive cash</strong> on the Riders page, then choose whether it was paid
              into the Lusuku MTN account (with the MTN transaction id) or is held as cash at
              hand; the rider is cleared at once. Staff can also adjust a rider&rsquo;s threshold. At weekly
              rider payout time, COD cash still held is offset against earnings so riders are
              never double-paid.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-money-coins" />
            6. How R&amp;W money moves
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            Customers pay by MTN MoMo, Airtel Money, card (via Flutterwave) or COD. Every
            successful payment triggers a Wallet Service escrow hold grouped per vendor. COD is
            the exception: the rider has already collected the cash, so its escrow holds and
            releases atomically — no 24-hour wait.
          </p>
          <ol className="text-muted mb-0" style={{ ...bodyText, paddingLeft: 20 }}>
            <li>
              <strong>Escrow.</strong> Funds sit in <code>pendingEscrow</code> on the vendor
              wallet until delivery is confirmed — the vendor sees the money as &ldquo;in
              transit&rdquo;.
            </li>
            <li>
              <strong>Release — first path wins.</strong> Either the customer confirms
              satisfaction (immediate release), or auto-release fires{" "}
              <strong>24 hours after delivery confirmation</strong> via an hourly job. Every
              delivery-confirmation path (rider, 3PL, vendor self-delivery) stamps the
              confirmation time that arms the auto-release.
            </li>
            <li>
              <strong>Commission at settlement.</strong> Retailer <strong>5%</strong>, Wholesaler{" "}
              <strong>3%</strong> — deducted when escrow credits the vendor wallet, so the vendor
              is credited the net amount.
            </li>
            <li>
              <strong>Maturity &amp; payout.</strong> Released funds mature into the withdrawable
              balance after <strong>3 days</strong>. Minimum withdrawal is{" "}
              <strong>UGX 500</strong>; payouts go out via MTN MoMo disbursement,
              vendor-requested or admin bulk.
            </li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bullet-list-67" />
            7. Dashboard surface area
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            Everything R&amp;W-related, with the admin sidebar path.
          </p>
          <Table responsive hover size="sm" className="mb-0" style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>Sidebar</th> <th>Path</th> <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>R &amp; W → Vendors</td>
                <td><code>/admin/rw-vendors</code></td>
                <td className="text-muted">Verify sellers, activate/deactivate, set <code>promoted</code></td>
              </tr>
              <tr>
                <td>Catalogue QC (hidden)</td>
                <td><code>/admin/edit-catalog-product/:id</code></td>
                <td className="text-muted">Staff Improve flow — same edit form vendors use</td>
              </tr>
              <tr>
                <td>Order details (hidden)</td>
                <td><code>/admin/retWholesalerOrderDetails/:id</code></td>
                <td className="text-muted">Rider dropdown shows COD LIMIT labels</td>
              </tr>
              <tr>
                <td>Vendor self-service</td>
                <td>
                  <code>/admin/retailerWholesalerOrdersTable</code>, <code>/admin/catalog</code>,{" "}
                  <code>/admin/rw-profile</code>
                </td>
                <td className="text-muted">Vendor-side orders, products &amp; store profile</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bulb-63" />
            8. Quick answers
          </Card.Title>
          <QA
            q="A vendor asks why they don&rsquo;t appear in Top Vendors — what are the criteria?"
            a={<>Active <strong>and</strong> manually promoted by staff. Being verified with products is not enough — set <code>promoted</code> from R &amp; W → Vendors.</>}
          />
          <QA
            q="A verified vendor is missing from the All Vendors page."
            a="They also need to be active and have at least one dashboard product. A verified vendor with zero products stays hidden until their first product exists."
          />
          <QA
            q="A vendor says they can&rsquo;t create products."
            a={<>Their seller verification is still <code>PENDING</code> (or <code>REJECTED</code>). Verify them from R &amp; W → Vendors; the write gate lifts immediately.</>}
          />
          <QA
            q="A vendor&rsquo;s dashboard product isn&rsquo;t showing on the main web shop."
            a="Expected — dashboard products and web-shop listings are two separate pipelines that never cross. The item must also be created as a web listing and pass its own admin verification."
          />
          <QA
            q="A rider suddenly stopped receiving assignments."
            a="Check for the red COD Limit badge — their held COD cash hit the threshold. They deposit via the MTN PIN prompt in the rider app and are unflagged automatically. If they cannot, take the cash and use Receive cash on the Riders page."
          />
          <QA
            q="A vendor asks where their money is after a delivered order."
            a="In escrow. It releases when the customer confirms satisfaction, or automatically 24 hours after delivery confirmation, then matures for 3 days before becoming withdrawable (commission already deducted: 5% retail, 3% wholesale)."
          />
          <QA
            q="A vendor&rsquo;s withdrawal was refused — what are the limits?"
            a="Minimum withdrawal is UGX 500, and only matured funds (3 days after escrow release) count toward the withdrawable balance."
          />
          {IS_DEV && (
            <div
              className="text-muted mt-2"
              style={{ fontSize: 12, borderTop: "1px solid #e5e9f1", paddingTop: 8 }}
            >
              <strong style={{ color: "#7a5a00" }}>DEV</strong> — exercise COD flagging locally by
              lowering a rider&rsquo;s per-rider threshold; the MTN deposit callback needs a
              publicly reachable tunnel to the wallet service in development.
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RetailWholesaleWiki;
